import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const HISTORY_STATUS_BADGE = {
  not_started: 'bg-secondary',
  in_progress: 'bg-info text-dark',
  passed: 'bg-success',
  failed: 'bg-danger',
  blocked: 'bg-warning text-dark',
  skipped: 'bg-secondary',
};

const HISTORY_STATUS_ICON = {
  not_started: '⏳', in_progress: '▶️', passed: '✅', failed: '❌', blocked: '🚫', skipped: '⏭️',
};

export default function TestCaseDetails() {
  const { projectKey, testCaseId } = useParams();
  const navigate = useNavigate();
  const [testCase, setTestCase] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDetails, setShowDetails] = useState(true);
  const [activeTab, setActiveTab] = useState('details'); // 'details' | 'history'

  // Test history state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyRunType, setHistoryRunType] = useState('all'); // 'all' | 'manual' | 'automated'

  // JIRA state
  const [jiraModal, setJiraModal] = useState(null); // 'link' | 'create' | null
  const [jiraIssueKey, setJiraIssueKey] = useState('');
  const [jiraProjectKey, setJiraProjectKey] = useState('');
  const [jiraIssueType, setJiraIssueType] = useState('Bug');
  const [jiraLoading, setJiraLoading] = useState(false);
  const [jiraError, setJiraError] = useState('');

  useEffect(() => {
    fetchTestCase();
    if (projectKey) {
      fetchProject();
    }
  }, [testCaseId, projectKey]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab, historyRunType]);

  const fetchHistory = async () => {
    try {
      setHistoryLoading(true);
      setHistoryError('');
      let url = `/runs/history/${testCaseId}`;
      if (historyRunType !== 'all') url += `?runType=${historyRunType}`;
      const res = await api.get(url);
      setHistory(res.data.data || []);
    } catch (err) {
      setHistoryError('Failed to load test history: ' + (err.response?.data?.error || err.message));
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchProject = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/projects/${projectKey}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const projectData = data.data || data;
        if (projectData) {
          setProject(projectData);
        }
      }
    } catch (err) {
      console.error('Error loading project:', err);
    }
  };

  const fetchTestCase = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/testcases/${testCaseId}`);
      setTestCase(response.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch test case: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/projects/${projectKey}/testcases?edit=${testCaseId}`);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this test case?')) {
      return;
    }

    try {
      await api.delete(`/testcases/${testCaseId}`);
      navigate(`/projects/${projectKey}/testcases`);
    } catch (err) {
      setError('Failed to delete test case: ' + (err.response?.data?.message || err.message));
    }
  };

  // ── JIRA Handlers ──────────────────────────────────────────────────────────

  const closeJiraModal = () => {
    setJiraModal(null);
    setJiraIssueKey('');
    setJiraProjectKey('');
    setJiraError('');
  };

  const handleJiraLink = async () => {
    if (!jiraIssueKey.trim()) { setJiraError('Enter a JIRA issue key'); return; }
    setJiraLoading(true);
    setJiraError('');
    try {
      await api.post(`/jira/testcases/${testCaseId}/link`, {
        issueKey: jiraIssueKey.trim().toUpperCase(),
      });
      closeJiraModal();
      fetchTestCase();
    } catch (err) {
      setJiraError(err.response?.data?.error || err.message);
    } finally {
      setJiraLoading(false);
    }
  };

  const handleJiraCreate = async () => {
    if (!jiraProjectKey.trim()) { setJiraError('Enter a JIRA project key'); return; }
    setJiraLoading(true);
    setJiraError('');
    try {
      await api.post(`/jira/testcases/${testCaseId}/create-issue`, {
        projectKey: jiraProjectKey.trim().toUpperCase(),
        issueType: jiraIssueType,
      });
      closeJiraModal();
      fetchTestCase();
    } catch (err) {
      setJiraError(err.response?.data?.error || err.message);
    } finally {
      setJiraLoading(false);
    }
  };

  const handleJiraSync = async () => {
    setJiraLoading(true);
    try {
      await api.post(`/jira/testcases/${testCaseId}/sync`);
      fetchTestCase();
    } catch (err) {
      setError('JIRA sync failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setJiraLoading(false);
    }
  };

  const handleJiraUnlink = async () => {
    if (!window.confirm('Unlink this JIRA issue from the test case?')) return;
    try {
      await api.delete(`/jira/testcases/${testCaseId}/link`);
      fetchTestCase();
    } catch (err) {
      setError('Failed to unlink: ' + (err.response?.data?.error || err.message));
    }
  };

  const getJiraStatusBadge = status => {
    if (!status) return <span className="badge bg-secondary">Unknown</span>;
    const s = status.toLowerCase();
    if (s.includes('done') || s.includes('closed') || s.includes('resolved'))
      return <span className="badge bg-success">{status}</span>;
    if (s.includes('progress') || s.includes('review'))
      return <span className="badge bg-primary">{status}</span>;
    if (s.includes('blocked')) return <span className="badge bg-danger">{status}</span>;
    return <span className="badge bg-secondary">{status}</span>;
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !testCase) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">{error || 'Test case not found'}</div>
        <Link to={`/projects/${projectKey}/testcases`} className="btn btn-secondary">
          Back to Test Cases
        </Link>
      </div>
    );
  }

  return (
    <>
    <div className="container mt-4">
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb mb-0" style={{ background: 'transparent', padding: 0 }}>
          <li className="breadcrumb-item">
            <Link to="/projects">Projects</Link>
          </li>
          {projectKey && project && (
            <li className="breadcrumb-item">
              <Link to={`/projects/${projectKey.toLowerCase()}`}>{project?.name}</Link>
            </li>
          )}
          <li className="breadcrumb-item">
            <Link to={`/projects/${projectKey}/testcases`}>Test Cases</Link>
          </li>
          <li className="breadcrumb-item active">{testCase.title}</li>
        </ol>
      </nav>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 style={{ marginTop: '0.5rem' }}>{testCase.title}</h2>
        <div className="btn-group" role="group">
          <button className="btn btn-outline-primary" onClick={handleEdit}>
            ✏️ Edit
          </button>
          <button className="btn btn-outline-danger" onClick={handleDelete}>
            🗑️ Delete
          </button>
          <Link to={`/projects/${projectKey}/testcases`} className="btn btn-secondary">
            ← Back
          </Link>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      {/* Tab navigation */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'details' ? 'active' : ''}`} onClick={() => setActiveTab('details')}>
            📋 Details
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>
            🕐 Execution History
          </button>
        </li>
      </ul>

      {activeTab === 'history' && (
        <div>
          {/* History filters */}
          <div className="d-flex gap-2 align-items-center mb-3 flex-wrap">
            <span className="text-muted small">Filter by run type:</span>
            {['all', 'manual', 'automated'].map(type => (
              <button
                key={type}
                className={`btn btn-sm ${historyRunType === type ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setHistoryRunType(type)}
              >
                {type === 'all' ? 'All' : type === 'manual' ? '👤 Manual' : '🤖 Automated'}
              </button>
            ))}
          </div>

          {historyError && <div className="alert alert-danger">{historyError}</div>}

          {historyLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border" role="status" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <div style={{ fontSize: '2.5rem' }}>📭</div>
              <p>No execution history found{historyRunType !== 'all' ? ` for ${historyRunType} runs` : ''}.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Run</th>
                    <th style={{ width: 80 }}>Type</th>
                    <th style={{ width: 120 }}>Status</th>
                    <th>Platform / Device</th>
                    <th>App Version</th>
                    <th>Environment</th>
                    <th>Executed By</th>
                    <th style={{ width: 160 }}>Executed At</th>
                    <th>Comment</th>
                    <th>Bug</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((item, idx) => {
                    const cfg = item.configuration || {};
                    const cicd = item.cicd || {};
                    return (
                      <tr key={idx}>
                        <td>
                          <Link
                            to={projectKey ? `/projects/${projectKey}/runs/${item.runId}` : `/runs/${item.runId}`}
                            className="text-decoration-none"
                          >
                            {item.runName}
                          </Link>
                          <div className="text-muted small">{new Date(item.runCreatedAt).toLocaleDateString()}</div>
                        </td>
                        <td>
                          <span className={`badge ${item.runType === 'automated' ? 'bg-primary' : 'bg-info text-dark'}`} style={{ fontSize: '0.7rem' }}>
                            {item.runType === 'automated' ? '🤖' : '👤'} {item.runType}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${HISTORY_STATUS_BADGE[item.status] || 'bg-secondary'}`}>
                            {HISTORY_STATUS_ICON[item.status]} {item.status?.replace('_', ' ')}
                          </span>
                        </td>
                        <td style={{ fontSize: '0.82rem' }}>
                          {cfg.platform && <div>{cfg.platform}{cfg.deviceName ? ` / ${cfg.deviceName}` : ''}</div>}
                          {cfg.osVersion && <div className="text-muted">{cfg.osVersion}</div>}
                          {item.runType === 'automated' && cicd.jobName && (
                            <div className="text-muted">{cicd.jobName} #{cicd.buildNumber}</div>
                          )}
                        </td>
                        <td style={{ fontSize: '0.82rem' }}>
                          {cfg.appVersion || '—'}
                          {cfg.buildNumber && <span className="text-muted"> (build {cfg.buildNumber})</span>}
                        </td>
                        <td style={{ fontSize: '0.82rem' }}>{cfg.environment || '—'}</td>
                        <td style={{ fontSize: '0.82rem' }}>
                          {typeof item.executedBy === 'object' ? item.executedBy?.username || '—' : item.executedBy || (item.runType === 'automated' ? cicd.triggeredBy || '—' : '—')}
                        </td>
                        <td style={{ fontSize: '0.82rem' }}>
                          {item.executedAt ? new Date(item.executedAt).toLocaleString() : '—'}
                        </td>
                        <td style={{ fontSize: '0.82rem' }}>
                          {item.comment || <span className="text-muted">—</span>}
                        </td>
                        <td>
                          {item.jiraIssueKey ? (
                            <a href={item.jiraIssueUrl} target="_blank" rel="noopener noreferrer" className="badge bg-danger text-decoration-none">
                              🐛 {item.jiraIssueKey}
                            </a>
                          ) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'details' && (<>
      <div className="card mb-4">
        <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => setShowDetails(!showDetails)}>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Test Case Details</h5>
            <span style={{ fontSize: '1.2rem' }}>
              {showDetails ? '▼' : '▶'}
            </span>
          </div>
        </div>
        {showDetails && (
        <div className="card-body">
          <div className="row">
            <div className="col-md-4 mb-3">
              <strong>ID:</strong>
              <div><code>{testCase.testCaseId || (testCase._id || testCase.id).toString().slice(-8)}</code></div>
            </div>

            <div className="col-md-4 mb-3">
              <strong>Priority:</strong>
              <div>
                <span className={`badge ${
                  testCase.priority === 'High' ? 'bg-danger' :
                  testCase.priority === 'Medium' ? 'bg-warning' :
                  'bg-secondary'
                }`}>
                  {testCase.priority || 'Medium'}
                </span>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <strong>Status:</strong>
              <div>
                <span className={`badge ${
                  testCase.status === 'Active' ? 'bg-success' :
                  testCase.status === 'Draft' ? 'bg-secondary' :
                  'bg-warning'
                }`}>
                  {testCase.status || 'Draft'}
                </span>
              </div>
            </div>
          </div>

          <div className="row">
            {testCase.category && (
              <div className="col-md-4 mb-3">
                <strong>Category:</strong>
                <div>{testCase.category}</div>
              </div>
            )}

            {testCase.severity && (
              <div className="col-md-4 mb-3">
                <strong>Severity:</strong>
                <div>{testCase.severity}</div>
              </div>
            )}

            {testCase.suiteId && (
              <div className="col-md-4 mb-3">
                <strong>Test Suite:</strong>
                <div>{typeof testCase.suiteId === 'object' ? testCase.suiteId?.name : testCase.suiteId}</div>
              </div>
            )}
          </div>

          <div className="row">
            {testCase.folderId && (
              <div className="col-md-4 mb-3">
                <strong>Folder:</strong>
                <div>{typeof testCase.folderId === 'object' ? testCase.folderId?.name : testCase.folderId}</div>
              </div>
            )}

            <div className="col-md-4 mb-3">
              <strong>Created:</strong>
              <div>{testCase.createdAt ? new Date(testCase.createdAt).toLocaleString() : 'N/A'}</div>
            </div>

            <div className="col-md-4 mb-3">
              <strong>Updated:</strong>
              <div>{testCase.updatedAt ? new Date(testCase.updatedAt).toLocaleString() : 'N/A'}</div>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* ── JIRA Card ── */}
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">🔗 JIRA Issue</h5>
          {testCase.jira?.issueKey && (
            <small className="text-muted">
              Last synced:{' '}
              {testCase.jira.syncedAt
                ? new Date(testCase.jira.syncedAt).toLocaleString()
                : 'Never'}
            </small>
          )}
        </div>
        <div className="card-body">
          {testCase.jira?.issueKey ? (
            <div>
              <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
                <a
                  href={testCase.jira.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-primary"
                >
                  🔗 {testCase.jira.issueKey}
                </a>
                {getJiraStatusBadge(testCase.jira.status)}
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleJiraSync}
                  disabled={jiraLoading}
                >
                  {jiraLoading ? (
                    <span className="spinner-border spinner-border-sm" role="status" />
                  ) : (
                    '🔄 Sync'
                  )}
                </button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={handleJiraUnlink}
                >
                  Unlink
                </button>
              </div>
            </div>
          ) : (
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => { setJiraIssueKey(''); setJiraError(''); setJiraModal('link'); }}
              >
                🔗 Link Existing Issue
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => { setJiraProjectKey(''); setJiraIssueType('Bug'); setJiraError(''); setJiraModal('create'); }}
              >
                ➕ Create JIRA Issue
              </button>
            </div>
          )}
        </div>
      </div>

      {testCase.description && (
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">Description</h5>
          </div>
          <div className="card-body">
            <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{testCase.description}</p>
          </div>
        </div>
      )}

      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Precondition</h5>
        </div>
        <div className="card-body">
          {testCase.precondition
            ? <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{testCase.precondition}</p>
            : <p className="mb-0 text-muted fst-italic">No precondition specified.</p>
          }
        </div>
      </div>

      {testCase.steps && (Array.isArray(testCase.steps) ? testCase.steps.length > 0 : testCase.steps) && (
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">Steps</h5>
          </div>
          <div className="card-body">
            <ol className="mb-0">
              {Array.isArray(testCase.steps) 
                ? testCase.steps.map((step, index) => (
                    <li key={index} className="mb-2">{step}</li>
                  ))
                : testCase.steps.split('\n').filter(s => s.trim()).map((step, index) => (
                    <li key={index} className="mb-2">{step}</li>
                  ))
              }
            </ol>
          </div>
        </div>
      )}

      {testCase.expectedResult && (
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">Expected Result</h5>
          </div>
          <div className="card-body">
            <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{testCase.expectedResult}</p>
          </div>
        </div>
      )}

      {testCase.linkedRequirements && testCase.linkedRequirements.length > 0 && (
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">Linked Requirements</h5>
          </div>
          <div className="card-body">
            <ul className="list-group list-group-flush">
              {testCase.linkedRequirements.map((req) => (
                <li key={req._id || req.id} className="list-group-item">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <Link
                        to={`/projects/${projectKey}/requirements/${req.reqId || req._id || req.id}`}
                        style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 500, marginRight: '8px' }}
                        title="View requirement details"
                      >
                        {req.reqId || `REQ-${String(req._id || req.id).slice(-5).padStart(3, '0')}`}
                      </Link>
                      <span>{req.title}</span>
                    </div>
                    <span className="badge bg-info">{req.status}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>)}

      {/* JIRA Link / Create Modal */}
      {jiraModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {jiraModal === 'link' ? '🔗 Link JIRA Issue' : '➕ Create JIRA Issue'}
                </h5>
                <button type="button" className="btn-close" onClick={closeJiraModal}></button>
              </div>
              <div className="modal-body">
                {jiraError && <div className="alert alert-danger">{jiraError}</div>}
                {jiraModal === 'link' ? (
                  <div className="mb-3">
                    <label className="form-label">JIRA Issue Key *</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. PROJ-123"
                      value={jiraIssueKey}
                      onChange={e => setJiraIssueKey(e.target.value.toUpperCase())}
                      onKeyDown={e => e.key === 'Enter' && handleJiraLink()}
                    />
                  </div>
                ) : (
                  <>
                    <div className="mb-3">
                      <label className="form-label">JIRA Project Key *</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. PROJ"
                        value={jiraProjectKey}
                        onChange={e => setJiraProjectKey(e.target.value.toUpperCase())}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Issue Type</label>
                      <select
                        className="form-select"
                        value={jiraIssueType}
                        onChange={e => setJiraIssueType(e.target.value)}
                      >
                        <option>Bug</option>
                        <option>Task</option>
                        <option>Story</option>
                        <option>Improvement</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeJiraModal} disabled={jiraLoading}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={jiraModal === 'link' ? handleJiraLink : handleJiraCreate}
                  disabled={jiraLoading}
                >
                  {jiraLoading ? (
                    <><span className="spinner-border spinner-border-sm me-2" role="status" />Working…</>
                  ) : jiraModal === 'link' ? 'Link Issue' : 'Create Issue'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
